import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { AIFeatureInfo, RoomEvent } from '../media/media'
import { RootState } from '../store'
import { selectAiFeatures } from './aiFeatures'
import { selectInputParametersHierarchyForSelectedMeetingTemplate } from './inputParametersHierarchy'
import { selectAiResults } from './aiResults'
import { selectSelectedMeetingTemplate } from './meetingTemplates'
import { MeetingTemplatePreset } from '../ai/meetingTemplate'

interface ExecutedAiFeaturesState {
  isLoading: boolean
  executedAiFeatureEvents: EntityState<RoomEvent, string>
  pollingEnabled: boolean
  pollingManuallyExecutedAiFeatureId: string | null
  isExecutionLoading: boolean
}

const executedAiFeatureEventsAdapter = createEntityAdapter<RoomEvent>()

const initialState: ExecutedAiFeaturesState = {
  isLoading: true,
  executedAiFeatureEvents: executedAiFeatureEventsAdapter.getInitialState(),
  pollingEnabled: false,
  pollingManuallyExecutedAiFeatureId: null,
  isExecutionLoading: false,
}

export const executedAiFeaturesSlice = createSlice({
  name: 'executedAiFeatures',
  initialState,
  reducers: {
    setExecutedAiFeaturesLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    addExecutedAiFeatureEvents: (state, action: PayloadAction<RoomEvent[]>) => {
      executedAiFeatureEventsAdapter.addMany(state.executedAiFeatureEvents, action.payload)
    },
    startExecutedAiFeaturesPolling: (state) => {
      state.pollingEnabled = true
    },
    stopExecutedAiFeaturesPolling: (state) => {
      state.pollingEnabled = false
    },
    setPollingManuallyExecutedAiFeatureId: (state, action: PayloadAction<string>) => {
      state.pollingManuallyExecutedAiFeatureId = action.payload
    },
    clearPollingManuallyExecutedAiFeatureId: (state) => {
      state.pollingManuallyExecutedAiFeatureId = null
    },
    setExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.isExecutionLoading = action.payload
    },
    resetExecutedAiFeatures: () => initialState,
  },
})

export const {
  setExecutedAiFeaturesLoading,
  addExecutedAiFeatureEvents,
  setPollingManuallyExecutedAiFeatureId,
  clearPollingManuallyExecutedAiFeatureId,
  startExecutedAiFeaturesPolling,
  stopExecutedAiFeaturesPolling,
  setExecutionLoading,
  resetExecutedAiFeatures,
} = executedAiFeaturesSlice.actions

const executedAiFeatureEventsSelectors = executedAiFeatureEventsAdapter.getSelectors(
  (state: RootState) => state.meetingSummary.executedAiFeatures.executedAiFeatureEvents,
)

export const selectExecutedAiFeatures = createSelector([executedAiFeatureEventsSelectors.selectAll], (roomEvents) => {
  const sortedUserAllocationUpdatedEvents = roomEvents
    .filter((event) => event.type === 'userAllocationUpdated')
    .sort((event1, event2) => {
      const { timestamp: timestamp1 } = event1
      const { timestamp: timestamp2 } = event2
      return timestamp1.localeCompare(timestamp2)
    })

  const latestUserAllocationUpdatedEventTimestamp = sortedUserAllocationUpdatedEvents.pop()?.timestamp
  return roomEvents
    .filter((event) => event.type === 'aiFeatureExecutionStarted')
    .filter((event) => !latestUserAllocationUpdatedEventTimestamp || event.timestamp > latestUserAllocationUpdatedEventTimestamp)
    .flatMap((event) =>
      (event.metadata?.executedAiFeatures ?? []).map((feature: AIFeatureInfo) => ({
        ...feature,
        timestamp: event.timestamp,
      })),
    )
})

export const selectExecutedAiFeaturesPollingEnabled = (state: RootState) => state.meetingSummary.executedAiFeatures.pollingEnabled

export const selectPollingManuallyExecutedAiFeatureId = (state: RootState) => state.meetingSummary.executedAiFeatures.pollingManuallyExecutedAiFeatureId

export const selectIsExecutionLoading = (state: RootState) => state.meetingSummary.executedAiFeatures.isExecutionLoading

export const selectNonExecutedPresetsForTemplate = createSelector(
  [selectExecutedAiFeatures, selectAiFeatures, selectInputParametersHierarchyForSelectedMeetingTemplate, selectAiResults, selectSelectedMeetingTemplate],
  (executedAiFeatures, aiFeatures, inputParametersHierarchy, allAiResults, selectSelectedMeetingTemplate): MeetingTemplatePreset[] => {
    if (!selectSelectedMeetingTemplate) {
      return []
    }
    return selectSelectedMeetingTemplate.presets.filter((preset) => {
      const { aiFeaturePresetId, aiFeatureId } = preset
      const aiFeatureWasExecutedWithInputParameter =
        aiFeatureId &&
        aiFeaturePresetId &&
        inputParametersHierarchy &&
        executedAiFeatures.some((executedFeature) => {
          const { id: executedAiFeatureId, presetId: executedAiFeaturePresetId, inputParametersHierarchyId: executedInputParameterId = null } = executedFeature
          const inputParameterHierarchyIdForPreset = inputParametersHierarchy.inputParametersHierarchyNodeByPresets[aiFeaturePresetId]?.id ?? null
          return (
            executedAiFeatureId === aiFeatureId &&
            executedAiFeaturePresetId === aiFeaturePresetId &&
            executedInputParameterId === inputParameterHierarchyIdForPreset
          )
        })
      const aiFeature = aiFeatures.find(({ id }) => id === aiFeatureId)
      return !aiFeatureWasExecutedWithInputParameter && aiFeature && allAiResults
    })
  },
)
